import { css } from "@emotion/react";
import React, { useEffect, useRef, useState } from "react";
import { Common } from "@src/styles/Common";
import { useFilterList, useFilterTextList, useFloorList, useMajorCategory } from "@src/pages/mapFilter/useFilterList";
import ShowSelectedUseLocValueBox from "@src/pages/mapFilter/ShowSelectedUseLocValueBox";
import AiTitle from "@src/components/aiFilter/AiTitle";
import { MAX_Y } from "@src/components/aiFilter/Constants";

interface Props {
    categories: string[];
    floor?: number | null;
    onChangeCategories: (key: string, val: string[]) => void;
    onChangeFloor?: (floor: number | null) => void;
    showToast?: () => void;
}

const useMajorCategoryWithoutAll = useMajorCategory.filter((v) => v.name !== "전체");

const PurposeSecondStep: React.FC<Props> = ({ categories, floor = null, onChangeCategories, onChangeFloor }) => {
    const [selectedMajor, setSelectedMajor] = useState<string>("");
    const showFloorFilter = selectedMajor === "상업/업무용" && categories[0] === "D02";
    const majorRef = useRef<any>();

    const getSelectedMajorName = (val: string[]) => {
        if (!val.length) return "";
        const code = val[0].slice(0, 1);
        const found = useMajorCategoryWithoutAll.find((c) => c.value === code);
        return found?.name ?? "";
    };

    useEffect(() => {
        if (categories.length === 0) {
            setSelectedMajor("");
            return;
        }
        setSelectedMajor(getSelectedMajorName(categories));
    }, [categories]);

    return (
        <div css={rootBoxStyle}>
            <AiTitle>
                <h2>
                    관심있는 <span className="color-text">용도</span>를 선택해주세요.
                </h2>
            </AiTitle>
            <div css={summaryBoxStyle}>
                {categories.length > 0 &&
                    (() => {
                        const val = categories[0];
                        const newText = useFilterTextList.find((text: any) => text.value === val);
                        return (
                            <ShowSelectedUseLocValueBox
                                value={newText?.name ?? val}
                                onDeleteValue={() => onChangeCategories("categories", [])}
                                key={val}
                            />
                        );
                    })()}
            </div>
            <div css={subTitleBoxStyle(showFloorFilter)}>
                <p className="subTitleText">대분류</p>
                <p className="subTitleText">중분류</p>
                {showFloorFilter && <p className="subTitleText">소분류</p>}
            </div>
            <div css={selectBoxRootStyle}>
                <div css={selectBoxStyle(MAX_Y, showFloorFilter)} ref={majorRef}>
                    {useMajorCategoryWithoutAll.map((category) => {
                        const checkActive =
                            categories[0] === category.value || (categories[0] && categories[0].startsWith(category.value));
                        return (
                            <button
                                key={category.name}
                                css={selectButtonStyle(!!checkActive)}
                                onClick={() => {
                                    if (checkActive) {
                                        onChangeCategories("categories", []);
                                        setSelectedMajor("");
                                    } else {
                                        onChangeCategories("categories", [category.value]);
                                        setSelectedMajor(category.name);
                                    }
                                    majorRef.current?.scrollTo(0, 0);
                                }}
                            >
                                {category.name}
                            </button>
                        );
                    })}
                </div>
                {selectedMajor && useFilterList[selectedMajor] && (
                    <div css={selectBoxStyle(MAX_Y, showFloorFilter)}>
                        {useFilterList[selectedMajor].map((category: any, i: number) => {
                            if (selectedMajor === "기타" && i > 0) return null;
                            const checkActive = categories[0] === category.value;
                            return (
                                <button
                                    key={`${category.name}${category.value}`}
                                    css={selectButtonStyle(checkActive)}
                                    onClick={() => {
                                        onChangeCategories("categories", checkActive ? [] : [category.value]);
                                    }}
                                >
                                    {category.name}
                                </button>
                            );
                        })}
                    </div>
                )}
                {showFloorFilter && onChangeFloor && (
                    <div css={selectBoxStyle(MAX_Y, true)}>
                        <button css={selectButtonStyle(floor == null)} onClick={() => onChangeFloor(null)} type="button">
                            선택안함
                        </button>
                        {useFloorList.map((item) => {
                            const valueNum = parseInt(item.value, 10);
                            const checkActive = floor === valueNum;
                            return (
                                <button
                                    key={item.value}
                                    css={selectButtonStyle(checkActive)}
                                    onClick={() => onChangeFloor(valueNum)}
                                    type="button"
                                >
                                    {item.name}
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default PurposeSecondStep;

const rootBoxStyle = css`
    background-color: #ffffff;
    font-size: 14px;
    padding: 0px 14px;
`;

const subTitleBoxStyle = (threeColumns?: boolean) => css`
    display: flex;
    align-items: center;
    border-bottom: 0.6px solid #b1aeb8;
    .subTitleText {
        padding: 8px 14px;
        background-color: #ffffff;
        color: #0c002c;
        text-align: center;
        font-size: 14px;
        font-weight: 500;
        line-height: 140%;
        box-sizing: border-box;
        flex: ${threeColumns ? "1" : "none"};
        width: ${threeColumns ? "auto" : "100%"};
    }
`;

const selectBoxRootStyle = css`
    display: flex;
    align-items: center;
`;

const selectBoxStyle = (MAXY: number, threeColumns?: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: flex-start;
    flex-direction: column;
    width: ${threeColumns ? "33.333%" : "50%"};
    padding: 12px 10px;
    gap: 8px;
    height: ${MAXY - 200}px;
    overflow-y: scroll;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const selectButtonStyle = (active: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 8px;
    padding: 10px;
    background: #fdfafa;
    transition: color 0.3s linear;
    font-size: 14px;
    font-weight: 500;
    ${active && {
        background: Common.colors.aucMarkerColor,
        color: "#ffffff",
    }}
`;

const summaryBoxStyle = css`
    padding: 8px 14px;
    display: flex;
    align-items: center;
    width: 100vw;
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;
